import React from 'react';
import { Box, Button, Card, CardContent, Chip, Theme, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TierChip from 'components/project/TierChip';
import { UnifiedRequest } from 'types/ManageUserMemberships';
import { formatDate } from 'utils/manageMemberships';

interface PendingRequestsTabProps {
  rows: UnifiedRequest[];
  searchText: string;
  isMobile: boolean;
  isLoading: boolean;
  theme: Theme;
  onReview: (row: UnifiedRequest) => void;
}

const PendingRequestsTab: React.FC<PendingRequestsTabProps> = ({
  rows,
  searchText,
  isMobile,
  isLoading,
  theme,
  onReview,
}) => {
  const columns: GridColDef[] = [
    {
      field: 'requestType',
      headerName: 'Type',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => (
        <Chip
          label={params.row.requestType === 'new-user' ? 'New User' : 'Upgrade'}
          size="small"
          color={params.row.requestType === 'new-user' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
    {
      field: 'affiliation',
      headerName: 'Affiliation',
      flex: 0.8,
      minWidth: 140,
      valueGetter: (params) => params.row.affiliation || '—',
    },
    {
      field: 'currentTier',
      headerName: 'Current Tier',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => (
        <TierChip tier={params.row.currentTier} />
      ),
    },
    {
      field: 'requestedTier',
      headerName: 'Requested Tier',
      flex: 0.6,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => {
        if (params.row.requestType === 'new-user') {
          return (
            <Typography variant="body2" color="text.disabled">
              N/A
            </Typography>
          );
        }
        return <TierChip tier={params.row.requestedTier} />;
      },
    },
    {
      field: 'justification',
      headerName: 'Justification',
      flex: 1.2,
      minWidth: 200,
      valueGetter: (params) => params.row.justification || '—',
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => {
        const val = params.row.justification;
        if (!val) {
          return (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          );
        }
        return (
          <Tooltip title={val} arrow>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'requestDate',
      headerName: 'Requested On',
      flex: 0.6,
      minWidth: 120,
      valueGetter: (params) =>
        params.row.requestDate ? new Date(params.row.requestDate).getTime() : -Infinity,
      sortComparator: (v1, v2) => (v1 as number) - (v2 as number),
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(params.row.requestDate)}
        </Typography>
      ),
    },
    {
      field: 'pendingActions',
      headerName: '',
      flex: 0.4,
      minWidth: 90,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<UnifiedRequest>) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => onReview(params.row)}
          sx={{ fontSize: '0.75rem' }}
        >
          Review
        </Button>
      ),
    },
  ];

  const renderCard = (row: UnifiedRequest): React.ReactElement => (
    <Card
      key={row.id}
      variant="outlined"
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${
          row.requestType === 'new-user' ? theme.palette.info.main : theme.palette.warning.main
        }`,
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {row.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.email}
            </Typography>
          </Box>
          <Chip
            label={row.requestType === 'new-user' ? 'New User' : 'Upgrade'}
            size="small"
            color={row.requestType === 'new-user' ? 'info' : 'warning'}
            variant="outlined"
          />
        </Box>
        {row.affiliation && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {row.affiliation}
          </Typography>
        )}
        {row.requestType === 'upgrade' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TierChip tier={row.currentTier} />
            <ArrowUpwardIcon sx={{ fontSize: 16, color: 'info.main' }} />
            <TierChip tier={row.requestedTier} />
          </Box>
        ) : (
          row.currentTier && (
            <Box sx={{ mb: 1 }}>
              <TierChip tier={row.currentTier} />
            </Box>
          )
        )}
        {row.justification && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Justification:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {row.justification}
            </Typography>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Requested: {formatDate(row.requestDate)}
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <Button variant="contained" size="small" onClick={() => onReview(row)}>
            Review
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      {isLoading ? null : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {rows.length} pending request{rows.length !== 1 ? 's' : ''}
          {searchText ? ` matching "${searchText}"` : ''}
        </Typography>
      )}
      {isLoading ? null : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {searchText
            ? `No pending requests matching "${searchText}".`
            : 'No pending requests at this time.'}
        </Typography>
      ) : isMobile ? (
        <Box>{rows.map(renderCard)}</Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row: UnifiedRequest) => row.id}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'requestDate', sort: 'desc' }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sortingOrder={['asc', 'desc']}
            disableRowSelectionOnClick
            disableColumnMenu
            autoHeight
            density="comfortable"
            sx={{
              fontSize: '0.95rem',
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
                visibility: 'visible',
                width: 'auto',
              },
              '& .MuiDataGrid-sortIcon': { opacity: '1 !important' },
            }}
          />
        </Box>
      )}
    </>
  );
};

export default PendingRequestsTab;